import * as React from "react"
import { format, parseISO } from "date-fns"
import { CalendarDays } from "lucide-react"

import { Button } from "@/components/ui/button"
import { Calendar } from "@/components/ui/calendar"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { cn } from "@/lib/utils"

function parseDate(value) {
  if (!value) {
    return undefined
  }

  try {
    return parseISO(value)
  } catch {
    return undefined
  }
}

function formatRangeLabel(fromDate, toDate) {
  if (!fromDate && !toDate) {
    return "Select date range"
  }

  if (fromDate && toDate) {
    return `${format(fromDate, "MMM d, yyyy")} - ${format(toDate, "MMM d, yyyy")}`
  }

  return format(fromDate || toDate, "MMM d, yyyy")
}

function formatIsoDate(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
    return ""
  }

  return new Date(date.getTime() - (date.getTimezoneOffset() * 60000)).toISOString().slice(0, 10)
}

function startOfToday() {
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  return today
}

function clampFutureDate(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
    return undefined
  }

  const today = startOfToday()
  return date > today ? today : date
}

const DateRangePicker = React.forwardRef(({
  value,
  onChange,
  onClear,
  placeholder = "Select date range",
  showValue = true,
  className,
  buttonClassName,
  contentClassName,
  presets,
}, ref) => {
  const [open, setOpen] = React.useState(false)
  const fromDate = React.useMemo(() => parseDate(value?.from_date), [value?.from_date])
  const toDate = React.useMemo(() => parseDate(value?.to_date), [value?.to_date])
  const selectedRange = React.useMemo(() => (showValue ? {
    from: fromDate,
    to: toDate,
  } : { from: undefined, to: undefined }), [fromDate, showValue, toDate])
  const [draftRange, setDraftRange] = React.useState(selectedRange)

  React.useEffect(() => {
    if (open) {
      setDraftRange(selectedRange)
    }
  }, [open, selectedRange])

  const handleSelect = React.useCallback((nextRange) => {
    setDraftRange(nextRange ? {
      from: clampFutureDate(nextRange.from),
      to: clampFutureDate(nextRange.to),
    } : { from: undefined, to: undefined })
  }, [])

  const handleApply = React.useCallback(() => {
    const nextFrom = draftRange?.from
    const nextTo = draftRange?.to || draftRange?.from

    if (!nextFrom) {
      onClear?.()
      onChange?.({ from_date: "", to_date: "" })
      setOpen(false)
      return
    }

    onChange?.({
      from_date: formatIsoDate(clampFutureDate(nextFrom)),
      to_date: formatIsoDate(clampFutureDate(nextTo)),
    })
    setOpen(false)
  }, [draftRange, onChange, onClear])

  const handleClear = React.useCallback(() => {
    setDraftRange({ from: undefined, to: undefined })
    if (onClear) {
      onClear()
    } else {
      onChange?.({ from_date: "", to_date: "" })
    }
    setOpen(false)
  }, [onChange, onClear])

  // Apply a quick preset range. Clears any manually selected calendar dates and
  // applies the preset's from/to, then closes the popover.
  const handlePreset = React.useCallback((preset) => {
    setDraftRange({ from: undefined, to: undefined })
    onChange?.({ from_date: preset.from_date, to_date: preset.to_date })
    setOpen(false)
  }, [onChange])

  return (
    <div ref={ref} className={cn("flex items-center", className)}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            className={cn(
              "h-10 min-w-[260px] justify-between border-border bg-card px-3 text-left font-normal hover:bg-card hover:text-foreground",
              buttonClassName
            )}
          >
            <span className="flex min-w-0 items-center gap-2">
              <CalendarDays className="h-4 w-4 shrink-0 text-sky-300" />
              <span className="truncate text-sm">{showValue ? formatRangeLabel(fromDate, toDate) : placeholder}</span>
            </span>
            <span className="rounded-md bg-background px-2 py-0.5 text-[10px] uppercase tracking-wide text-muted-foreground">
              Range
            </span>
          </Button>
        </PopoverTrigger>
        <PopoverContent align="end" className={cn("w-auto border-border bg-card p-0", contentClassName)}>
          <div className="border-b border-border px-4 py-3">
            <p className="text-sm font-medium">Select range</p>
            <p className="mt-1 text-xs text-muted-foreground">Drag across dates or click start and end dates.</p>
          </div>
          {Array.isArray(presets) && presets.length > 0 && (
            <div className="flex flex-wrap gap-2 border-b border-border px-4 py-3">
              {presets.map((preset) => (
                <Button
                  key={preset.label}
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-7 text-xs"
                  onClick={() => handlePreset(preset)}
                >
                  {preset.label}
                </Button>
              ))}
            </div>
          )}
          <Calendar
            mode="range"
            numberOfMonths={1}
            defaultMonth={(showValue && (fromDate || toDate)) || new Date()}
            selected={draftRange}
            onSelect={handleSelect}
            disabled={{ after: startOfToday() }}
            className="p-3"
          />
          <div className="flex items-center justify-between gap-2 border-t border-border px-4 py-3">
            <Button type="button" variant="ghost" size="sm" onClick={handleClear}>
              Clear
            </Button>
            <Button type="button" size="sm" onClick={handleApply}>
              Apply Range
            </Button>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  )
})

DateRangePicker.displayName = "DateRangePicker"

export { DateRangePicker }
